import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ShoppingCart, Search, Plus, Minus, Trash2, Send, Package, Users, ChevronDown, ArrowLeft, Percent, Tag, Check, Lock, Zap, Grid3X3, List } from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import UpgradePromptCard from '../../components/common/UpgradePromptCard';
import { wholesalerAPI, catalogDiscountsAPI, flashOffersAPI, ordersAPI, API_URL } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { getImageUrl } from '../../utils/imageHelper';
import axios from 'axios';

// ── Helper: get auth headers ──
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const OnBehalfOrder = () => {
  const { t } = useTranslation('wholesaler');
  const { user } = useAuth();
  const navigate = useNavigate();

  // Step: 'select-business' → 'browse-products' → 'review'
  const [step, setStep] = useState('select-business');

  // Business selection
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessSearch, setBusinessSearch] = useState('');
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);

  // Products (paginated)
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [productPage, setProductPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const PRODUCTS_PER_PAGE = 20;

  // Discounts for selected business
  const [customerDiscounts, setCustomerDiscounts] = useState([]);
  const [catalogDiscounts, setCatalogDiscounts] = useState(null);
  const [productDiscountMap, setProductDiscountMap] = useState({});

  // Flash offers — { [productId]: { offerPrice, originalPrice, offerId, offerTitle } }
  const [flashOfferMap, setFlashOfferMap] = useState({});

  // Cart
  const [cart, setCart] = useState({});

  // View mode (grid / list) — persisted
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('onBehalf_viewMode') || 'grid');
  useEffect(() => { localStorage.setItem('onBehalf_viewMode', viewMode); }, [viewMode]);

  // ERP pricing
  const [erpPricing, setErpPricing] = useState(null); // { lines, totals }
  const [erpLoading, setErpLoading] = useState(false);

  // Order
  const [notes, setNotes] = useState('');
  const [deliveryType, setDeliveryType] = useState('delivery');
  const [requestedDate, setRequestedDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ── Draft persistence (sessionStorage) ──
  // Keeps cart + business selection + form fields alive across refreshes and
  // accidental back-navigation. Scoped per user; cleared on submit or reset.
  // `hydrated` is a state (not a ref) so that the save effect re-runs AFTER
  // the hydration setStates have propagated — a ref would let the first save
  // fire with stale empty state and overwrite the saved draft.
  const draftKey = `onBehalf_draft_${user?._id || user?.id || 'anon'}`;
  const [draftHydrated, setDraftHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(draftKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved.selectedBusiness) setSelectedBusiness(saved.selectedBusiness);
        if (saved.step) setStep(saved.step);
        if (saved.cart) setCart(saved.cart);
        if (saved.notes) setNotes(saved.notes);
        if (saved.deliveryType) setDeliveryType(saved.deliveryType);
        if (saved.requestedDate) setRequestedDate(saved.requestedDate);
      }
    } catch { /* ignore corrupt draft */ }
    setDraftHydrated(true);
  }, [draftKey]);

  useEffect(() => {
    if (!draftHydrated) return;
    try {
      sessionStorage.setItem(draftKey, JSON.stringify({
        selectedBusiness, step, cart, notes, deliveryType, requestedDate
      }));
    } catch { /* quota exceeded — ignore */ }
  }, [draftHydrated, draftKey, selectedBusiness, step, cart, notes, deliveryType, requestedDate]);

  const clearDraft = () => {
    try { sessionStorage.removeItem(draftKey); } catch {}
  };

  // ── Fetch approved businesses ──
  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        setLoadingBusinesses(true);
        const res = await wholesalerAPI.getCatalogRequests();
        let approved = res.data.approvedBusinesses || [];

        // If sales_rep with assigned accounts, filter to assigned only
        const assignedIds = user?.teamContext?.assignedBusinessIds || [];
        if (user?.teamContext?.subRole === 'sales_rep' && assignedIds.length > 0) {
          const assignedSet = new Set(assignedIds.map(id => id.toString()));
          approved = approved.filter(b => assignedSet.has(b.businessId?.toString()));
        }

        setBusinesses(approved);
      } catch (err) {
        toast.error(t('onBehalf.errorLoadingBusinesses', 'Failed to load businesses'));
      } finally {
        setLoadingBusinesses(false);
      }
    };
    fetchBusinesses();
  }, [user, t]);

  // ── Fetch products (paginated) ──
  const fetchProducts = useCallback(async (page = 1, search = '', category = 'all', append = false) => {
    try {
      if (page === 1) setLoadingProducts(true);
      else setLoadingMore(true);

      const params = { page, limit: PRODUCTS_PER_PAGE };
      if (search.trim()) params.search = search.trim();
      if (category && category !== 'all') params.category = category;

      const res = await wholesalerAPI.getProducts(params);
      const data = res.data;

      // Handle paginated response — show both active + inactive (sales reps can pick hidden products)
      if (data.products && data.pagination) {
        const newProducts = data.products.filter(p => p.status !== 'deleted' && p.status !== 'discontinued');
        setProducts(prev => append ? [...prev, ...newProducts] : newProducts);
        setTotalProducts(data.pagination.total);
        setHasMoreProducts(data.pagination.page < data.pagination.pages);
        setProductPage(data.pagination.page);
      } else {
        // Backwards-compat: raw array
        const all = Array.isArray(data) ? data : (data.products || []);
        setProducts(all.filter(p => p.status === 'active' || p.status === 'inactive'));
        setHasMoreProducts(false);
        setTotalProducts(all.length);
      }
    } catch (err) {
      toast.error(t('onBehalf.errorLoadingProducts', 'Failed to load products'));
    } finally {
      setLoadingProducts(false);
      setLoadingMore(false);
    }
  }, [t]);

  // Load products when entering browse step
  useEffect(() => {
    if (step === 'browse-products' && products.length === 0) {
      fetchProducts(1, productSearch, categoryFilter);
    }
  }, [step]); // eslint-disable-line

  // Reload on search/category change (debounced)
  useEffect(() => {
    if (step !== 'browse-products') return;
    const timer = setTimeout(() => {
      fetchProducts(1, productSearch, categoryFilter);
    }, 400);
    return () => clearTimeout(timer);
  }, [productSearch, categoryFilter]); // eslint-disable-line

  const loadMoreProducts = () => {
    if (!loadingMore && hasMoreProducts) {
      fetchProducts(productPage + 1, productSearch, categoryFilter, true);
    }
  };

  // ── Fetch discounts when business is selected ──
  useEffect(() => {
    if (!selectedBusiness) return;

    const wholesalerId = user?.teamContext?.wholesalerId;
    if (!wholesalerId) return;

    const fetchDiscounts = async () => {
      try {
        // Customer discounts
        const custRes = await axios.get(
          `${API_URL}/discounts/customers/business/${selectedBusiness.businessId}/details?wholesalerId=${wholesalerId}`,
          { headers: getAuthHeaders() }
        );
        if (custRes.data.success && custRes.data.hasDiscounts) {
          setCustomerDiscounts(custRes.data.discounts.map(d => ({
            active: true,
            percentage: d.percentage,
            reason: d.reason,
            minimumOrderAmount: d.minimumOrderAmount || 0,
            targetType: d.targetType || 'all',
            targetValue: d.targetValue || null
          })));
        } else {
          setCustomerDiscounts([]);
        }
      } catch { setCustomerDiscounts([]); }

      try {
        // Catalog discounts
        const catRes = await catalogDiscountsAPI.getForWholesaler(wholesalerId);
        setCatalogDiscounts(catRes.data || null);
      } catch { setCatalogDiscounts(null); }

      try {
        // Product-specific discounts for this business
        // Endpoint returns { discounts: [...] } (no success flag)
        const prodRes = await wholesalerAPI.getProductDiscountsForBusiness(selectedBusiness.businessId);
        const discArr = prodRes.data.discounts || prodRes.data || [];
        const map = {};
        (Array.isArray(discArr) ? discArr : []).forEach(d => {
          if (d.productId) map[d.productId._id || d.productId] = d;
        });
        setProductDiscountMap(map);
      } catch { setProductDiscountMap({}); }

      // Flash offers — fetch active offers for this wholesaler
      try {
        const flashRes = await flashOffersAPI.getByWholesaler(wholesalerId);
        const offers = flashRes.data?.offers || [];
        const fMap = {};
        offers.forEach(offer => {
          (offer.items || []).forEach(item => {
            const pid = item.productId?._id || item.productId;
            if (pid) {
              fMap[pid.toString()] = {
                offerPrice: item.offerPrice,
                originalPrice: item.originalPrice,
                offerId: offer._id,
                offerTitle: offer.title || ''
              };
            }
          });
        });
        setFlashOfferMap(fMap);
      } catch { setFlashOfferMap({}); }
    };

    fetchDiscounts();
  }, [selectedBusiness, user]);

  // ── Combined discount calculator (mirrors WholesalerDetail logic) ──
  const activeCustomerDiscount = useMemo(() => {
    if (customerDiscounts.length === 0) return { active: false, percentage: 0, targeted: [] };
    // For on-behalf, show all discounts (no cart total threshold filtering)
    const flat = customerDiscounts.filter(d => !d.targetType || d.targetType === 'all');
    const targeted = customerDiscounts.filter(d => d.targetType && d.targetType !== 'all');
    const flatPct = Math.min(flat.reduce((sum, d) => sum + d.percentage, 0), 100);
    return { active: flat.length > 0 || targeted.length > 0, percentage: flatPct, targeted };
  }, [customerDiscounts]);

  const getProductCatalogPct = useCallback((product) => {
    if (!catalogDiscounts?.discounts?.length) return 0;
    const applicable = catalogDiscounts.discounts.map(d => {
      if (d.type === 'flat') return { pct: d.percentage || 0, stackable: d.stackable };
      if (d.type === 'targeted') {
        const match =
          (d.targetType === 'brand' && (product.brand || '').toLowerCase() === (d.targetValue || '').toLowerCase()) ||
          (d.targetType === 'category' && (product.category || '').toLowerCase() === (d.targetValue || '').toLowerCase()) ||
          (d.targetType === 'subcategory' && (product.subcategory || '').toLowerCase() === (d.targetValue || '').toLowerCase());
        return match ? { pct: d.targetPercentage || 0, stackable: d.stackable } : null;
      }
      return null;
    }).filter(Boolean);
    if (applicable.length === 0) return 0;
    const stackable = applicable.filter(a => a.stackable);
    const nonStackable = applicable.filter(a => !a.stackable);
    const stackedPct = stackable.reduce((sum, a) => sum + (a.pct || 0), 0);
    const bestNonStackable = nonStackable.reduce((best, a) => (a.pct || 0) > best ? (a.pct || 0) : best, 0);
    return Math.round(Math.min(Math.max(stackedPct, bestNonStackable), 100) * 100) / 100;
  }, [catalogDiscounts]);

  const getProductDiscount = useCallback((product) => {
    const catalogPct = getProductCatalogPct(product);
    let customerPct = activeCustomerDiscount.active ? activeCustomerDiscount.percentage : 0;

    // Add targeted customer discounts
    if (activeCustomerDiscount.targeted?.length > 0) {
      for (const td of activeCustomerDiscount.targeted) {
        if (td.targetType === 'category' && (product.category || '').toLowerCase() === (td.targetValue || '').toLowerCase()) {
          customerPct += td.percentage;
        } else if (td.targetType === 'subcategory' && (product.subcategory || '').toLowerCase() === (td.targetValue || '').toLowerCase()) {
          customerPct += td.percentage;
        }
      }
      if (customerPct > 100) customerPct = 100;
    }

    const productPct = productDiscountMap[product._id]?.percentage || 0;
    const effectivePct = productPct > 0 ? Math.max(productPct, customerPct) : customerPct;

    if (catalogPct === 0 && effectivePct === 0) return { active: false, percentage: 0, catalogPercentage: 0, customerPercentage: 0, productPercentage: 0 };
    const combined = Math.round((catalogPct + effectivePct) * 100) / 100;
    return { active: true, percentage: Math.min(combined, 100), catalogPercentage: catalogPct, customerPercentage: productPct > 0 ? 0 : customerPct, productPercentage: productPct };
  }, [getProductCatalogPct, activeCustomerDiscount, productDiscountMap]);

  // ── Price helpers ──
  const getPrice = (product) => product.pricing?.unitPrice || product.pricing?.basePrice || product.price || 0;
  const getUnit = (product) => product.pricing?.unit || product.unit || 'piece';
  const getMinQty = (product) => product.pricing?.minOrderQuantity || 1;

  const getFlashOffer = useCallback((product) => {
    return flashOfferMap[product._id] || null;
  }, [flashOfferMap]);

  const getDisplayPrice = useCallback((product) => {
    const original = getPrice(product);
    const flash = getFlashOffer(product);
    const discount = getProductDiscount(product);

    // Flash offer = final price — no other discounts stacked on top
    if (flash) {
      return { original, display: flash.offerPrice, hasDiscount: true, discount, isFlash: true, flash };
    }

    if (!discount.active) return { original, display: original, hasDiscount: false, discount, isFlash: false, flash: null };
    const display = Math.round(original * (1 - discount.percentage / 100) * 100) / 100;
    return { original, display, hasDiscount: true, discount, isFlash: false, flash: null };
  }, [getProductDiscount, getFlashOffer]);

  // ── Filtered data ──
  const filteredBusinesses = useMemo(() => {
    if (!businessSearch.trim()) return businesses;
    const q = businessSearch.toLowerCase();
    return businesses.filter(b =>
      (b.businessName || '').toLowerCase().includes(q) ||
      (b.vatNumber || '').includes(q)
    );
  }, [businesses, businessSearch]);

  // Categories fetched from all products for filter dropdown
  const [allCategories, setAllCategories] = useState([]);
  useEffect(() => {
    if (step !== 'browse-products') return;
    // Fetch all products once just for categories (no pagination)
    const fetchCategories = async () => {
      try {
        const res = await wholesalerAPI.getProducts({ status: 'active' });
        const all = Array.isArray(res.data) ? res.data : (res.data.products || []);
        const cats = [...new Set(all.map(p => p.category).filter(Boolean))].sort();
        setAllCategories(cats);
      } catch { /* ignore */ }
    };
    if (allCategories.length === 0) fetchCategories();
  }, [step]); // eslint-disable-line

  // ── Cart helpers ──
  // Cart shape: { [productId]: { quantity, product } }
  // Caching the product alongside the qty keeps cart items alive across
  // pagination/search/category changes — otherwise items would vanish from
  // `cartItems` the moment the user searched for something else, even though
  // they were still in cart state.
  const cartItems = useMemo(() => {
    return Object.values(cart)
      .filter(entry => entry && entry.quantity > 0 && entry.product)
      .map(({ product, quantity }) => ({ ...product, quantity }));
  }, [cart]);

  // Gross subtotal — for flash items, use flash price as the "base" since flash
  // replaces the unit price (not treated as a separate discount line). Other
  // discounts (catalog, customer, product) are shown separately.
  const cartGross = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const flash = getFlashOffer(item);
      const { isFlash } = getDisplayPrice(item);
      const price = (flash && isFlash) ? flash.offerPrice : getPrice(item);
      return sum + (price * item.quantity);
    }, 0);
  }, [cartItems, getFlashOffer, getDisplayPrice]);

  // Net total after applying per-item discounts + flash offers. Flash offer
  // price replaces discounted price when it's a better deal.
  const cartTotal = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const { display } = getDisplayPrice(item);
      return sum + Math.round(display * item.quantity * 100) / 100;
    }, 0);
  }, [cartItems, getDisplayPrice]);

  const cartDiscountAmount = useMemo(
    () => Math.round((cartGross - cartTotal) * 100) / 100,
    [cartGross, cartTotal]
  );

  // ERP-derived totals — when ERP pricing is active, the summary must use the
  // per-line ERP prices (not the platform unitPrice), otherwise the subtotal /
  // discount line shown to the wholesaler would not match the per-line column.
  const erpTotals = useMemo(() => {
    if (!erpPricing?.lines?.length) return null;
    let gross = 0;
    let net = 0;
    erpPricing.lines.forEach(line => {
      const qty = line.quantity || 0;
      gross += (line.unitPrice || 0) * qty;
      net += (line.lineTotal != null ? line.lineTotal : (line.unitPrice || 0) * qty);
    });
    gross = Math.round(gross * 100) / 100;
    net = Math.round(net * 100) / 100;
    return { gross, net, discount: Math.round((gross - net) * 100) / 100 };
  }, [erpPricing]);

  const cartCount = cartItems.length;

  // updateCart/setCartQty accept either a product object (for new adds) or a
  // productId string (for existing items — product ref is already cached).
  const updateCart = (productOrId, delta) => {
    setCart(prev => {
      const id = typeof productOrId === 'string' ? productOrId : productOrId._id;
      const existing = prev[id];
      const productRef = existing?.product || (typeof productOrId === 'object' ? productOrId : null);
      const current = existing?.quantity || 0;
      const next = Math.max(0, current + delta);
      if (next === 0 || !productRef) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: { quantity: next, product: productRef } };
    });
  };

  const setCartQty = (productOrId, qty) => {
    const val = Math.max(0, parseInt(qty) || 0);
    setCart(prev => {
      const id = typeof productOrId === 'string' ? productOrId : productOrId._id;
      const existing = prev[id];
      const productRef = existing?.product || (typeof productOrId === 'object' ? productOrId : null);
      if (val === 0 || !productRef) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: { quantity: val, product: productRef } };
    });
  };

  const removeFromCart = (productId) => {
    setCart(prev => { const { [productId]: _, ...rest } = prev; return rest; });
  };

  // ── ERP pricing at review step ──
  useEffect(() => {
    if (step !== 'review' || cartItems.length === 0 || !selectedBusiness) return;
    const wholesalerId = user?.teamContext?.wholesalerId;
    if (!wholesalerId) return;

    const fetchErp = async () => {
      try {
        setErpLoading(true);
        const res = await ordersAPI.calculateErp({
          wholesalerId,
          businessId: selectedBusiness.businessId,
          items: cartItems.map(item => ({ productId: item._id, quantity: item.quantity }))
        });
        if (res.data?.erpAvailable && res.data.pricing) {
          setErpPricing(res.data.pricing);
        } else {
          setErpPricing(null);
        }
      } catch {
        setErpPricing(null);
      } finally {
        setErpLoading(false);
      }
    };
    fetchErp();
  }, [step, cartItems, selectedBusiness, user]);

  // ERP-aware price lookup for a cart item
  const getErpLinePrice = useCallback((productId) => {
    if (!erpPricing?.lines) return null;
    return erpPricing.lines.find(l => l.productId === productId) || null;
  }, [erpPricing]);

  // ── Submit order ──
  const handleSubmit = async () => {
    if (!selectedBusiness || cartItems.length === 0) return;
    try {
      setSubmitting(true);
      // Build items with flash offer metadata when applicable
      const orderItems = cartItems.map(item => {
        const flash = getFlashOffer(item);
        const { isFlash } = getDisplayPrice(item);
        const entry = { productId: item._id, quantity: item.quantity };
        if (flash && isFlash) {
          entry.flashOfferId = flash.offerId;
          entry.unitPrice = flash.offerPrice;
          entry.originalPrice = getPrice(item);
        }
        return entry;
      });
      const flashOfferIds = [...new Set(orderItems.filter(i => i.flashOfferId).map(i => i.flashOfferId))];

      const delivery = {
        type: deliveryType,
        ...(requestedDate && { scheduledFor: new Date(requestedDate).toISOString() })
      };
      const res = await wholesalerAPI.placeOnBehalfOrder({
        businessId: selectedBusiness.businessId,
        items: orderItems,
        ...(flashOfferIds.length > 0 && { flashOfferIds }),
        notes: notes.trim() || undefined,
        delivery
      });
      if (res.data.success) {
        clearDraft();
        toast.success(t('onBehalf.orderPlaced', 'Order placed successfully!'));
        navigate('/wholesaler/orders');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || t('onBehalf.orderError', 'Failed to place order'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Select business ──
  const selectBusiness = (business) => {
    setSelectedBusiness(business);
    setStep('browse-products');
  };

  // ── Access gating ──
  // Office sub-role cannot place on-behalf orders. Show a friendly access
  // denied screen instead of letting the page render and 403 from the API.
  const subRole = user?.teamContext?.subRole || 'manager';
  if (subRole === 'office') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center shadow-sm">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-gray-500" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              {t('onBehalf.accessDenied.title', 'Not available for your role')}
            </h1>
            <p className="text-sm text-gray-600 mb-6">
              {t('onBehalf.accessDenied.body', 'On-behalf ordering is only available to managers and sales representatives. Ask your manager if you need access.')}
            </p>
            <button
              onClick={() => navigate('/wholesaler/dashboard')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
            >
              {t('onBehalf.accessDenied.back', 'Back to dashboard')}
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Starter-tier wholesalers cannot use on-behalf ordering — show the
  // shared upgrade prompt that links to the pricing page.
  const subscriptionTier = user?.teamContext?.subscriptionTier;
  if (subscriptionTier === 'starter') {
    return (
      <UpgradePromptCard
        badge={t('onBehalf.upgrade.badge', 'Professional & Enterprise')}
        title={t('onBehalf.upgrade.title', 'Place orders on behalf of your customers')}
        body={t('onBehalf.upgrade.body', 'Upgrade to Professional or Enterprise to let your team place orders for any approved business — perfect for phone orders, sales reps in the field, and account managers.')}
        ctaLabel={t('onBehalf.upgrade.cta', 'View plans & upgrade')}
        backLabel={t('onBehalf.upgrade.back', 'Back to dashboard')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            {step !== 'select-business' && (
              <button
                onClick={() => {
                  if (step === 'review') setStep('browse-products');
                  else { setStep('select-business'); setSelectedBusiness(null); setCart({}); setCustomerDiscounts([]); setCatalogDiscounts(null); setProductDiscountMap({}); clearDraft(); }
                }}
                className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h1 className="text-2xl font-bold text-gray-900">
              {t('onBehalf.title', 'Place Order on Behalf')}
            </h1>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className={step === 'select-business' ? 'text-blue-600 font-semibold' : 'text-gray-400'}>
              1. {t('onBehalf.stepBusiness', 'Select Business')}
            </span>
            <span className="text-gray-300">→</span>
            <span className={step === 'browse-products' ? 'text-blue-600 font-semibold' : 'text-gray-400'}>
              2. {t('onBehalf.stepProducts', 'Add Products')}
            </span>
            <span className="text-gray-300">→</span>
            <span className={step === 'review' ? 'text-blue-600 font-semibold' : 'text-gray-400'}>
              3. {t('onBehalf.stepReview', 'Review & Submit')}
            </span>
          </div>
        </div>

        {/* ============ STEP 1: Select Business ============ */}
        {step === 'select-business' && (
          <div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('onBehalf.searchBusiness', 'Search businesses...')}
                value={businessSearch}
                onChange={e => setBusinessSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {loadingBusinesses ? (
              <div className="text-center py-12 text-gray-500">{t('common:loading', 'Loading...')}</div>
            ) : filteredBusinesses.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">{t('onBehalf.noBusinesses', 'No approved businesses found')}</p>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {filteredBusinesses.map(b => (
                  <button
                    key={b.businessId}
                    onClick={() => selectBusiness(b)}
                    className="text-left p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all"
                  >
                    <p className="font-semibold text-gray-900">{b.businessName}</p>
                    {b.vatNumber && <p className="text-sm text-gray-500 mt-1">VAT: {b.vatNumber}</p>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ============ STEP 2: Browse Products ============ */}
        {step === 'browse-products' && (
          <div>
            {/* Banner: selected business + discount summary */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <p className="text-sm text-blue-800">
                  <span className="font-medium">{t('onBehalf.orderingFor', 'Ordering for')}:</span>{' '}
                  {selectedBusiness?.businessName}
                </p>
                {activeCustomerDiscount.active && (
                  <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                    <Percent className="w-3 h-3" />
                    {t('onBehalf.customerDiscount', 'Customer discount')}: {activeCustomerDiscount.percentage}%
                  </p>
                )}
                {catalogDiscounts?.discounts?.length > 0 && (
                  <p className="text-xs text-teal-700 mt-1 flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    {t('onBehalf.catalogDiscount', 'Catalog discount')}: {catalogDiscounts.discounts.map(d =>
                      d.type === 'flat' ? `${d.percentage}%` :
                      d.type === 'targeted' ? `${d.targetPercentage}% (${d.targetValue})` :
                      d.name || `${d.percentage}%`
                    ).join(', ')}
                  </p>
                )}
                {Object.keys(productDiscountMap).length > 0 && (
                  <p className="text-xs text-purple-700 mt-1 flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    {t('onBehalf.productDiscounts', 'Product discounts')}: {Object.keys(productDiscountMap).length} {t('onBehalf.items', 'items')}
                  </p>
                )}
                {Object.keys(flashOfferMap).length > 0 && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    {t('onBehalf.flashOffers', 'Flash offers')}: {Object.keys(flashOfferMap).length} {t('onBehalf.items', 'items')}
                  </p>
                )}
              </div>
              {cartCount > 0 && (
                <button
                  onClick={() => setStep('review')}
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  <ShoppingCart className="w-4 h-4" />
                  {t('onBehalf.reviewOrder', 'Review Order')} ({cartCount})
                </button>
              )}
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('onBehalf.searchProducts', 'Search products...')}
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="relative">
                <select
                  value={categoryFilter}
                  onChange={e => setCategoryFilter(e.target.value)}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">{t('onBehalf.allCategories', 'All Categories')}</option>
                  {allCategories.map(cat => (
                    <option key={cat} value={cat}>{t(`products:categories.${cat}`, cat)}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Product count + view toggle */}
            <div className="flex items-center justify-between mb-3">
              {totalProducts > 0 && !loadingProducts ? (
                <p className="text-sm text-gray-500">{totalProducts} {t('onBehalf.productsFound', 'products')}</p>
              ) : <span />}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                  title={t('onBehalf.gridView', 'Grid view')}
                  aria-label={t('onBehalf.gridView', 'Grid view')}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                  title={t('onBehalf.listView', 'List view')}
                  aria-label={t('onBehalf.listView', 'List view')}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            {loadingProducts ? (
              <div className="text-center py-12 text-gray-500">{t('common:loading', 'Loading...')}</div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">{t('onBehalf.noProducts', 'No products found')}</p>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid gap-2 sm:gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {products.map(product => {
                  const inCart = cart[product._id]?.quantity || 0;
                  const { original, display, hasDiscount, discount, isFlash } = getDisplayPrice(product);
                  const savings = Math.round((original - display) * 100) / 100;
                  const isInStock = product.availability?.inStock !== false;

                  return (
                    <div
                      key={product._id}
                      className={`group bg-white rounded-lg border overflow-hidden hover:shadow-md transition-all ${
                        inCart > 0 ? 'border-blue-400 ring-1 ring-blue-100' : 'border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      {/* Clickable image */}
                      <div className="relative aspect-square bg-gray-50 overflow-hidden cursor-pointer" onClick={() => navigate(`/products/${product._id}`)}>
                        {product.images?.[0] ? (
                          <img
                            src={getImageUrl(product.images[0])}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-8 h-8 sm:w-10 sm:h-10 text-gray-300" />
                          </div>
                        )}

                        {/* Discount / Flash badges */}
                        {hasDiscount && (
                          <div className="absolute top-1.5 left-1.5 flex flex-col gap-1">
                            {isFlash && (
                              <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded shadow-sm inline-flex items-center gap-0.5">
                                <Zap className="w-2.5 h-2.5" /> Flash
                              </span>
                            )}
                            {!isFlash && discount.productPercentage > 0 && (
                              <span className="px-1.5 py-0.5 bg-purple-600 text-white text-[10px] font-bold rounded shadow-sm inline-flex items-center gap-0.5">
                                <Tag className="w-2.5 h-2.5" />-{discount.productPercentage}%
                              </span>
                            )}
                            {!isFlash && (discount.catalogPercentage > 0 || discount.customerPercentage > 0) && (
                              <span className="px-1.5 py-0.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white text-[10px] font-bold rounded shadow-sm">
                                -{Math.round((discount.catalogPercentage + discount.customerPercentage) * 100) / 100}%
                              </span>
                            )}
                          </div>
                        )}

                        {!isInStock && (
                          <div className="absolute bottom-1.5 left-1.5">
                            <span className="px-1.5 py-0.5 bg-red-500/90 text-white text-[10px] font-medium rounded">
                              {t('catalog.outOfStock', 'Out of Stock')}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="p-2 sm:p-3">
                        {/* Category */}
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5 truncate">
                          {t(`products:categories.${product.category}`, product.category)}
                        </p>

                        {/* Clickable name (hidden products: no navigation — public detail 404s) */}
                        <h3
                          className={`text-xs sm:text-sm font-semibold text-gray-900 mb-1 sm:mb-1.5 line-clamp-2 leading-tight ${product.status === 'inactive' ? '' : 'cursor-pointer hover:text-blue-600 transition-colors'}`}
                          onClick={product.status === 'inactive' ? undefined : () => navigate(`/products/${product._id}`)}
                        >
                          {product.name}
                          {product.status === 'inactive' && (
                            <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-gray-200 text-gray-600 rounded font-medium align-middle">
                              {t('onBehalf.hiddenBadge', 'Hidden')}
                            </span>
                          )}
                        </h3>

                        {/* SKU */}
                        {product.sku && <p className="text-[10px] text-gray-400 mb-1 truncate">SKU: {product.sku}</p>}

                        {/* Price */}
                        <div className="flex items-baseline flex-wrap gap-x-1 gap-y-0.5 mb-1.5">
                          <span className={`text-base sm:text-lg font-bold ${isFlash ? 'text-red-600' : hasDiscount ? 'text-emerald-600' : 'text-gray-900'}`}>
                            €{display.toFixed(2)}
                          </span>
                          {hasDiscount && (
                            <span className="text-[10px] text-red-400 line-through">€{original.toFixed(2)}</span>
                          )}
                          <span className="text-[10px] sm:text-xs text-gray-500">
                            /{t(`products:unitShort.${getUnit(product)}`, getUnit(product))}
                          </span>
                        </div>

                        {/* Savings badge (desktop only, to save mobile space) */}
                        {hasDiscount && savings > 0 && (
                          <div className="mb-2 hidden sm:block">
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-medium rounded">
                              <Check className="w-2.5 h-2.5" />
                              {t('onBehalf.save', 'Save')} €{savings.toFixed(2)}
                            </span>
                          </div>
                        )}

                        {/* Stock indicator */}
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            (product.stockStatus || 'in_stock') === 'in_stock' ? 'bg-emerald-500' :
                            (product.stockStatus || 'in_stock') === 'low_stock' ? 'bg-amber-500' : 'bg-red-500'
                          }`} />
                          <span className="text-[10px] text-gray-500 truncate">
                            {(product.stockStatus || 'in_stock') === 'in_stock' ? t('catalog.inStock', 'In Stock') :
                             product.stockStatus === 'low_stock' ? t('catalog.lowStock', 'Low Stock') : t('catalog.outOfStock', 'Out of Stock')}
                          </span>
                          {getMinQty(product) > 1 && (
                            <span className="text-[10px] text-gray-400 ml-auto flex-shrink-0">Min: {getMinQty(product)}</span>
                          )}
                        </div>

                        {/* Add to cart */}
                        {isInStock && inCart === 0 && (
                          <button
                            onClick={() => updateCart(product, getMinQty(product))}
                            className="w-full py-1.5 sm:py-2 bg-blue-600 text-white rounded-md text-xs sm:text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
                          >
                            <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            {t('onBehalf.addToOrder', 'Add')}
                          </button>
                        )}

                        {isInStock && inCart > 0 && (
                          <div className="flex items-center gap-1 sm:gap-2">
                            <button onClick={() => updateCart(product, -1)} className="p-1 sm:p-1.5 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors flex-shrink-0" title={t('onBehalf.decrease', 'Decrease')}>
                              <Minus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                            <input
                              type="number"
                              value={inCart}
                              onChange={e => setCartQty(product, e.target.value)}
                              className="w-full min-w-0 text-center border border-gray-300 rounded-md py-1 text-xs sm:text-sm"
                              min="0"
                            />
                            <button onClick={() => updateCart(product, 1)} className="p-1 sm:p-1.5 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors flex-shrink-0" title={t('onBehalf.increase', 'Increase')}>
                              <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                            <button onClick={() => removeFromCart(product._id)} className="hidden sm:block p-1.5 rounded-md text-red-500 hover:bg-red-50 transition-colors" title={t('onBehalf.remove', 'Remove')}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-1.5">
                {products.map(product => {
                  const inCart = cart[product._id]?.quantity || 0;
                  const { original, display, hasDiscount, discount, isFlash } = getDisplayPrice(product);
                  const isInStock = product.availability?.inStock !== false;
                  const stockStatus = product.stockStatus || 'in_stock';

                  return (
                    <div
                      key={product._id}
                      className={`bg-white border rounded-lg hover:shadow-sm transition-all px-2 py-2 sm:px-3 sm:py-2.5 ${
                        inCart > 0 ? 'border-blue-400 ring-1 ring-blue-100' : 'border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        {/* Thumbnail */}
                        <div
                          className="w-10 h-10 sm:w-12 sm:h-12 rounded-md bg-gray-50 overflow-hidden flex-shrink-0 cursor-pointer"
                          onClick={() => navigate(`/products/${product._id}`)}
                        >
                          {product.images?.[0] ? (
                            <img src={getImageUrl(product.images[0])} alt={product.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><Package className="w-4 h-4 text-gray-300" /></div>
                          )}
                        </div>

                        {/* Name + meta */}
                        <div className="flex-1 min-w-0">
                          <h3
                            className={`text-xs sm:text-sm font-semibold text-gray-900 leading-tight line-clamp-2 sm:line-clamp-1 ${product.status === 'inactive' ? '' : 'cursor-pointer hover:text-blue-600 transition-colors'}`}
                            onClick={product.status === 'inactive' ? undefined : () => navigate(`/products/${product._id}`)}
                          >
                            {product.name}
                            {product.status === 'inactive' && (
                              <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-gray-200 text-gray-600 rounded font-medium align-middle">
                                {t('onBehalf.hiddenBadge', 'Hidden')}
                              </span>
                            )}
                          </h3>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                              stockStatus === 'in_stock' ? 'bg-emerald-500' : stockStatus === 'low_stock' ? 'bg-amber-500' : 'bg-red-500'
                            }`} />
                            {product.sku && <span className="text-[10px] text-gray-400">#{product.sku}</span>}
                            {isFlash && (
                              <span className="px-1 py-0.5 bg-red-500 text-white text-[9px] font-bold rounded inline-flex items-center gap-0.5">
                                <Zap className="w-2 h-2" />Flash
                              </span>
                            )}
                            {!isFlash && hasDiscount && (discount.catalogPercentage + discount.customerPercentage + discount.productPercentage) > 0 && (
                              <span className="px-1 py-0.5 bg-emerald-500 text-white text-[9px] font-bold rounded">
                                -{Math.round((discount.catalogPercentage + discount.customerPercentage + discount.productPercentage) * 100) / 100}%
                              </span>
                            )}
                            {getMinQty(product) > 1 && (
                              <span className="text-[10px] text-gray-400">Min: {getMinQty(product)}</span>
                            )}
                          </div>
                        </div>

                        {/* Price */}
                        <div className="flex-shrink-0 text-right">
                          <div className={`text-sm sm:text-base font-bold ${isFlash ? 'text-red-600' : hasDiscount ? 'text-emerald-600' : 'text-gray-900'}`}>
                            €{display.toFixed(2)}
                          </div>
                          {hasDiscount && (
                            <div className="text-[10px] text-red-400 line-through leading-none">€{original.toFixed(2)}</div>
                          )}
                          <div className="text-[10px] text-gray-500 leading-none mt-0.5">/{t(`products:unitShort.${getUnit(product)}`, getUnit(product))}</div>
                        </div>

                        {/* Cart controls */}
                        <div className="flex-shrink-0">
                          {!isInStock ? (
                            <span className="text-[10px] text-red-500 font-medium">{t('catalog.outOfStock', 'Out of Stock')}</span>
                          ) : inCart === 0 ? (
                            <button
                              onClick={() => updateCart(product, getMinQty(product))}
                              className="px-2.5 py-1.5 sm:px-3 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors flex items-center gap-1"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              <span className="hidden sm:inline">{t('onBehalf.addToOrder', 'Add')}</span>
                            </button>
                          ) : (
                            <div className="flex items-center gap-1">
                              <button onClick={() => updateCart(product, -1)} className="p-1 rounded bg-gray-100 hover:bg-gray-200 transition-colors">
                                <Minus className="w-3.5 h-3.5" />
                              </button>
                              <input
                                type="number"
                                value={inCart}
                                onChange={e => setCartQty(product, e.target.value)}
                                className="w-10 sm:w-12 text-center border border-gray-300 rounded py-0.5 text-xs"
                                min="0"
                              />
                              <button onClick={() => updateCart(product, 1)} className="p-1 rounded bg-gray-100 hover:bg-gray-200 transition-colors">
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Load More */}
            {hasMoreProducts && !loadingProducts && products.length > 0 && (
              <div className="text-center mt-6">
                <button
                  onClick={loadMoreProducts}
                  disabled={loadingMore}
                  className="px-6 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {loadingMore ? t('common:loading', 'Loading...') : t('common:buttons.loadMore', 'Load More')}
                </button>
              </div>
            )}

            {/* Floating cart bar */}
            {cartCount > 0 && (
              <div className="fixed inset-x-0 bottom-4 flex justify-center px-4 pointer-events-none z-50">
                <div className="bg-blue-600 text-white rounded-full shadow-lg px-6 py-3 flex items-center gap-4 pointer-events-auto">
                  <ShoppingCart className="w-5 h-5" />
                  <span className="font-medium">
                    {cartCount} {cartCount === 1 ? t('onBehalf.item', 'item') : t('onBehalf.items', 'items')} · €{cartTotal.toFixed(2)}
                  </span>
                  <button
                    onClick={() => setStep('review')}
                    className="bg-white text-blue-600 font-semibold px-4 py-1.5 rounded-full text-sm hover:bg-blue-50 transition-colors"
                  >
                    {t('onBehalf.review', 'Review')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============ STEP 3: Review & Submit ============ */}
        {step === 'review' && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
              <h2 className="text-sm font-medium text-gray-500 mb-1">{t('onBehalf.orderingFor', 'Ordering for')}</h2>
              <p className="text-lg font-semibold text-gray-900">{selectedBusiness?.businessName}</p>
              {activeCustomerDiscount.active && (
                <p className="text-sm text-emerald-600 mt-1 flex items-center gap-1">
                  <Percent className="w-3.5 h-3.5" />
                  {t('onBehalf.customerDiscount', 'Customer discount')}: {activeCustomerDiscount.percentage}%
                </p>
              )}
            </div>

            {/* ERP loading indicator */}
            {erpLoading && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-700 flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                {t('onBehalf.erpCalculating', 'Calculating ERP pricing...')}
              </div>
            )}

            {/* ERP pricing active banner */}
            {erpPricing && !erpLoading && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-700 flex items-center gap-2">
                <Check className="w-4 h-4" />
                {t('onBehalf.erpActive', 'ERP pricing applied — prices reflect your ERP policy')}
              </div>
            )}

            <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 mb-4">
              <div className="p-4">
                <h2 className="font-semibold text-gray-900">{t('onBehalf.orderItems', 'Order Items')} ({cartCount})</h2>
              </div>
              {cartItems.map(item => {
                const erpLine = getErpLinePrice(item._id);
                const { original, display, hasDiscount, discount, isFlash } = getDisplayPrice(item);
                // For ERP rows: erpLine.unitPrice is the GROSS unit price and
                // erpLine.lineTotal is the already-discounted line total. The
                // discounted per-unit price is lineTotal/quantity, NOT unitPrice.
                const erpNetUnit = erpLine && erpLine.quantity
                  ? Math.round((erpLine.lineTotal / erpLine.quantity) * 100) / 100
                  : null;
                const erpHasDiscount = !!(erpLine && erpNetUnit != null && erpNetUnit < (erpLine.unitPrice || 0));
                const erpDisplayPrice = erpHasDiscount ? erpNetUnit : (erpLine ? erpLine.unitPrice : null);
                const effectivePrice = erpLine ? erpDisplayPrice : display;
                // Per-line column: ERP uses its own line total. Flash offers show
                // the flash price × qty directly (like Cart.js). Other discounts
                // are summarized at the bottom so lines show gross.
                const lineTotal = erpLine
                  ? erpLine.lineTotal
                  : isFlash
                    ? Math.round(display * item.quantity * 100) / 100
                    : Math.round(original * item.quantity * 100) / 100;
                const showErpBadge = !!erpLine;
                return (
                  <div key={item._id} className="p-4 flex items-center gap-4">
                    <div
                      className="w-12 h-12 bg-gray-100 rounded-md overflow-hidden flex-shrink-0 cursor-pointer"
                      onClick={() => navigate(`/products/${item._id}`)}
                    >
                      {item.images?.[0] ? (
                        <img src={getImageUrl(item.images[0])} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Package className="w-5 h-5 text-gray-300" /></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-blue-600" onClick={() => navigate(`/products/${item._id}`)}>{item.name}</p>
                        {showErpBadge && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[9px] font-semibold rounded">ERP</span>}
                        {!showErpBadge && isFlash && <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[9px] font-semibold rounded inline-flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" /> Flash</span>}
                      </div>
                      <p className="text-xs text-gray-500">
                        {showErpBadge ? (
                          erpHasDiscount ? (
                            <><span className="text-blue-600">€{effectivePrice.toFixed(2)}</span> <span className="line-through text-gray-400">€{erpLine.unitPrice.toFixed(2)}</span></>
                          ) : (
                            <span className="text-blue-600">€{effectivePrice.toFixed(2)}</span>
                          )
                        ) : isFlash ? (
                          <><span className="text-red-600">€{display.toFixed(2)}</span> <span className="line-through text-gray-400">€{original.toFixed(2)}</span></>
                        ) : hasDiscount ? (
                          <><span className="text-emerald-600">€{display.toFixed(2)}</span> <span className="line-through text-gray-400">€{original.toFixed(2)}</span></>
                        ) : (
                          <>€{display.toFixed(2)}</>
                        )}
                        {' × '}{item.quantity}
                      </p>
                      {/* Per-item discount badges (platform discounts only — hidden for ERP and flash) */}
                      {!showErpBadge && !isFlash && hasDiscount && (
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {discount.catalogPercentage > 0 && (
                            <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-medium rounded">
                              {t('onBehalf.catalogDiscount', 'Catalog')} -{discount.catalogPercentage}%
                            </span>
                          )}
                          {discount.productPercentage > 0 && (
                            <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 text-[10px] font-medium rounded">
                              {t('cart.productDiscount', 'Product Discount')} -{discount.productPercentage}%
                            </span>
                          )}
                          {discount.customerPercentage > 0 && (
                            <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-medium rounded">
                              {t('onBehalf.customerDiscount', 'Customer')} -{discount.customerPercentage}%
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => updateCart(item._id, -1)} className="p-1 rounded hover:bg-gray-100"><Minus className="w-4 h-4" /></button>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={e => setCartQty(item._id, e.target.value)}
                        className="w-14 text-center border border-gray-300 rounded py-0.5 text-sm"
                        min="0"
                      />
                      <button onClick={() => updateCart(item._id, 1)} className="p-1 rounded hover:bg-gray-100"><Plus className="w-4 h-4" /></button>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 w-20 text-right">€{lineTotal.toFixed(2)}</p>
                    <button onClick={() => removeFromCart(item._id)} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                );
              })}
              <div className="p-4 bg-gray-50 space-y-2">
                {(() => {
                  // Prefer ERP totals when ERP pricing is active so the summary
                  // reflects the same per-line prices shown above.
                  const summaryGross = erpTotals ? erpTotals.gross : cartGross;
                  const summaryDiscount = erpTotals ? erpTotals.discount : cartDiscountAmount;
                  const summaryTotal = erpTotals ? erpTotals.net : cartTotal;
                  return (
                    <>
                      <div className="flex justify-between items-center text-sm text-gray-700">
                        <span>{t('onBehalf.subtotal', 'Subtotal')}</span>
                        <span className="font-medium">€{summaryGross.toFixed(2)}</span>
                      </div>
                      {summaryDiscount > 0 && (
                        <div className="flex justify-between items-center text-sm text-emerald-700">
                          <span>{t('onBehalf.discounts', 'Discounts')}</span>
                          <span className="font-medium">−€{summaryDiscount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                        <span className="font-semibold text-gray-900">{t('onBehalf.total', 'Total')}</span>
                        <span className="text-lg font-bold text-gray-900">€{summaryTotal.toFixed(2)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('onBehalf.notes', 'Order Notes')} ({t('onBehalf.optional', 'optional')})
              </label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder={t('onBehalf.notesPlaceholder', 'Add any notes for this order...')}
              />
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('onBehalf.deliveryMethod', 'Delivery Method')}
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setDeliveryType('delivery')}
                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                      deliveryType === 'delivery'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t('onBehalf.typeDelivery', 'Delivery')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeliveryType('pickup')}
                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                      deliveryType === 'pickup'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t('onBehalf.typePickup', 'Pickup')}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('onBehalf.requestedDate', 'Requested Date')} ({t('onBehalf.optional', 'optional')})
                </label>
                <input
                  type="date"
                  value={requestedDate}
                  onChange={e => setRequestedDate(e.target.value)}
                  onClick={(e) => e.target.showPicker?.()}
                  onFocus={(e) => e.target.showPicker?.()}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep('browse-products')}
                className="flex-1 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('onBehalf.addMore', 'Add More Items')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || cartItems.length === 0}
                className="flex-1 py-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <span>{t('onBehalf.placing', 'Placing Order...')}</span>
                ) : (
                  <><Send className="w-4 h-4" />{t('onBehalf.placeOrder', 'Place Order')}</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default OnBehalfOrder;
